/**
 * Marketplace Integrations Page
 * HRIS, PEO, Payroll, and Benefits integrations management
 *
 * Features:
 * - Browse available integrations catalog
 * - Install and configure integrations
 * - OAuth flow handling
 * - Field mapping configuration
 * - Sync management and history
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { useTabbedChrome } from '@/components/weavein';
import {
  Store,
  Plus,
  Settings,
  RefreshCw,
  Loader2,
  AlertCircle,
  CheckCircle,
  XCircle,
  Play,
  Pause,
  Trash2,
  Eye,
  Link2,
  Unlink,
  Clock,
  Database,
  ArrowRight,
  Search,
  Filter,
  ChevronRight,
  ExternalLink,
  Key,
  Shield,
  Zap,
  Calendar,
  Activity,
  Users,
  Building2,
  FileText,
  X,
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  marketplaceApi,
  MarketplaceInstallation,
  MarketplaceFieldMapping,
  MarketplaceSyncHistory,
  AvailableIntegration,
} from '../../shared/services/platformApi';
import { config as productConfig } from '@/src/config';

// ============================================================================
// TYPES
// ============================================================================

type MarketplaceTab = 'installed' | 'available' | 'sync-history';

// ============================================================================
// CONSTANTS - Available Integrations (fallback if API fails)
// ============================================================================

const FALLBACK_INTEGRATIONS: AvailableIntegration[] = [
  {
    id: 'bamboohr',
    name: 'BambooHR',
    description: 'Leading HRIS for small and medium businesses. Sync employees, departments, and org structure.',
    category: 'hris',
    auth_type: 'api_key',
    features: ['Employee sync', 'Department sync', 'Time-off data', 'Custom fields'],
    is_popular: true,
  },
  {
    id: 'adp_workforce',
    name: 'ADP Workforce Now',
    description: 'Enterprise HCM platform. Comprehensive employee and payroll data sync.',
    category: 'hris',
    auth_type: 'oauth2',
    features: ['Employee sync', 'Payroll data', 'Benefits enrollment', 'Time tracking'],
    is_popular: true,
  },
  {
    id: 'workday',
    name: 'Workday',
    description: 'Enterprise cloud HCM. Full workforce management and benefits sync.',
    category: 'hris',
    auth_type: 'oauth2',
    features: ['Employee sync', 'Benefits', 'Compensation', 'Org structure'],
    is_popular: true,
  },
  {
    id: 'ukg_pro',
    name: 'UKG Pro',
    description: 'Unified HCM suite for enterprise workforce management.',
    category: 'hris',
    auth_type: 'oauth2',
    features: ['Employee sync', 'Scheduling', 'Time & attendance', 'Benefits'],
    is_popular: false,
  },
  {
    id: 'adp_totalsource',
    name: 'ADP TotalSource',
    description: 'Professional Employer Organization (PEO) services with full HR outsourcing.',
    category: 'peo',
    auth_type: 'oauth2',
    features: ['Client management', 'Employee sync', 'Payroll', 'Benefits admin'],
    is_popular: true,
  },
  {
    id: 'paychex_peo',
    name: 'Paychex PEO',
    description: 'PEO services for small to mid-size businesses.',
    category: 'peo',
    auth_type: 'api_key',
    features: ['Client sync', 'Employee data', 'Payroll processing', 'HR services'],
    is_popular: false,
  },
  {
    id: 'trinet',
    name: 'TriNet',
    description: 'Full-service HR solutions and PEO for SMBs.',
    category: 'peo',
    auth_type: 'oauth2',
    features: ['Employee sync', 'Benefits', 'Payroll', 'Compliance'],
    is_popular: false,
  },
  {
    id: 'gusto',
    name: 'Gusto',
    description: 'Modern payroll, benefits, and HR platform for small businesses.',
    category: 'payroll',
    auth_type: 'oauth2',
    features: ['Payroll sync', 'Benefits', 'Onboarding', 'Time tracking'],
    is_popular: true,
  },
  {
    id: 'paylocity',
    name: 'Paylocity',
    description: 'Cloud-based payroll and HCM software.',
    category: 'payroll',
    auth_type: 'api_key',
    features: ['Payroll data', 'Tax filing', 'Time & labor', 'Benefits'],
    is_popular: false,
  },
  {
    id: 'microsoft_teams',
    name: 'Microsoft Teams',
    description: 'Team collaboration and communication platform.',
    category: 'communication',
    auth_type: 'oauth2',
    features: ['User sync', 'Channel creation', 'Notifications', 'Bot integration'],
    is_popular: true,
  },
  {
    id: 'slack',
    name: 'Slack',
    description: 'Business communication and collaboration platform.',
    category: 'communication',
    auth_type: 'oauth2',
    features: ['User sync', 'Channel management', 'Notifications', 'Workflows'],
    is_popular: true,
  },
];

const CATEGORY_INFO: Record<string, { label: string; icon: React.ComponentType<{ className?: string }>; color: string }> = {
  hris: { label: 'HRIS', icon: Users, color: 'bg-blue-100 text-blue-700' },
  peo: { label: 'PEO', icon: Building2, color: 'bg-purple-100 text-purple-700' },
  payroll: { label: 'Payroll', icon: FileText, color: 'bg-green-100 text-green-700' },
  benefits: { label: 'Benefits', icon: Shield, color: 'bg-orange-100 text-orange-700' },
  communication: { label: 'Communication', icon: Zap, color: 'bg-pink-100 text-pink-700' },
  crm: { label: 'CRM', icon: Database, color: 'bg-cyan-100 text-cyan-700' },
};

// ============================================================================
// INSTALLATION CARD
// ============================================================================

interface InstallationCardProps {
  installation: MarketplaceInstallation;
  onConfigure: () => void;
  onSync: () => void;
  onPause: () => void;
  onResume: () => void;
  onDisconnect: () => void;
  onViewHistory: () => void;
}

function InstallationCard({
  installation,
  onConfigure,
  onSync,
  onPause,
  onResume,
  onDisconnect,
  onViewHistory,
}: InstallationCardProps) {
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-700 border-green-300';
      case 'paused': return 'bg-yellow-100 text-yellow-700 border-yellow-300';
      case 'failed': return 'bg-red-100 text-red-700 border-red-300';
      case 'pending': return 'bg-blue-100 text-blue-700 border-blue-300';
      default: return 'bg-gray-100 text-gray-700 border-gray-300';
    }
  };

  const category = CATEGORY_INFO[installation.category] || CATEGORY_INFO.hris;
  const CategoryIcon = category.icon;

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-3">
          <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${category.color}`}>
            <CategoryIcon className="w-6 h-6" />
          </div>
          <div>
            <h3 className="font-semibold text-gray-900">{installation.integration_name}</h3>
            <span className={`inline-flex items-center px-2 py-0.5 text-xs rounded border ${getStatusColor(installation.status)}`}>
              {installation.status}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-1">
          {installation.status === 'active' && (
            <button
              onClick={onSync}
              className="p-1.5 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded"
              title="Sync now"
            >
              <RefreshCw className="w-4 h-4" />
            </button>
          )}
          <button
            onClick={onConfigure}
            className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded"
            title="Configure"
          >
            <Settings className="w-4 h-4" />
          </button>
          <button
            onClick={onViewHistory}
            className="p-1.5 text-gray-500 hover:text-purple-600 hover:bg-purple-50 rounded"
            title="Sync history"
          >
            <Clock className="w-4 h-4" />
          </button>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-3 text-sm mb-3">
        <div>
          <p className="text-gray-500">Total Syncs</p>
          <p className="font-medium text-gray-900">{installation.total_syncs}</p>
        </div>
        <div>
          <p className="text-gray-500">Records</p>
          <p className="font-medium text-gray-900">{installation.records_synced.toLocaleString()}</p>
        </div>
        <div>
          <p className="text-gray-500">Last Sync</p>
          <p className="font-medium text-gray-900">
            {installation.last_sync_at
              ? new Date(installation.last_sync_at).toLocaleDateString()
              : 'Never'}
          </p>
        </div>
      </div>

      {installation.last_error && (
        <div className="bg-red-50 border border-red-200 rounded p-2 mb-3">
          <p className="text-xs text-red-700 line-clamp-2">{installation.last_error}</p>
        </div>
      )}

      <div className="flex items-center justify-between pt-3 border-t border-gray-100">
        <div className="text-xs text-gray-500">
          {installation.sync_frequency} sync • {installation.auth_type}
        </div>
        <div className="flex items-center gap-2">
          {installation.status === 'active' ? (
            <button
              onClick={onPause}
              className="px-3 py-1 text-xs text-yellow-700 hover:bg-yellow-50 rounded"
            >
              Pause
            </button>
          ) : installation.status === 'paused' ? (
            <button
              onClick={onResume}
              className="px-3 py-1 text-xs text-green-700 hover:bg-green-50 rounded"
            >
              Resume
            </button>
          ) : null}
          <button
            onClick={onDisconnect}
            className="px-3 py-1 text-xs text-red-700 hover:bg-red-50 rounded"
          >
            Disconnect
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// AVAILABLE INTEGRATION CARD
// ============================================================================

interface AvailableIntegrationCardProps {
  integration: AvailableIntegration;
  isInstalled: boolean;
  onInstall: () => void;
}

function AvailableIntegrationCard({ integration, isInstalled, onInstall }: AvailableIntegrationCardProps) {
  const category = CATEGORY_INFO[integration.category] || CATEGORY_INFO.hris;
  const CategoryIcon = category.icon;

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
      <div className="flex items-start gap-3 mb-3">
        <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${category.color}`}>
          <CategoryIcon className="w-6 h-6" />
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <h3 className="font-semibold text-gray-900">{integration.name}</h3>
            {integration.is_popular && (
              <span className="px-1.5 py-0.5 text-xs bg-yellow-100 text-yellow-800 rounded">Popular</span>
            )}
          </div>
          <span className={`inline-flex items-center px-2 py-0.5 text-xs rounded ${category.color} mt-1`}>
            {category.label}
          </span>
        </div>
      </div>

      <p className="text-sm text-gray-600 mb-3 line-clamp-2">{integration.description}</p>

      <div className="flex flex-wrap gap-1 mb-4">
        {integration.features.slice(0, 4).map((feature) => (
          <span key={feature} className="px-2 py-0.5 text-xs bg-gray-100 text-gray-600 rounded">
            {feature}
          </span>
        ))}
      </div>

      <div className="flex items-center justify-between pt-3 border-t border-gray-100">
        <div className="flex items-center gap-1 text-xs text-gray-500">
          {integration.auth_type === 'oauth2' ? (
            <>
              <Shield className="w-3 h-3" />
              <span>OAuth 2.0</span>
            </>
          ) : (
            <>
              <Key className="w-3 h-3" />
              <span>API Key</span>
            </>
          )}
        </div>
        <button
          onClick={onInstall}
          disabled={isInstalled}
          className={`px-4 py-1.5 text-sm rounded-lg flex items-center gap-1 ${
            isInstalled
              ? 'bg-gray-100 text-gray-500 cursor-not-allowed'
              : 'bg-blue-600 text-white hover:bg-blue-700'
          }`}
        >
          {isInstalled ? (
            <>
              <CheckCircle className="w-4 h-4" />
              Installed
            </>
          ) : (
            <>
              <Plus className="w-4 h-4" />
              Install
            </>
          )}
        </button>
      </div>
    </div>
  );
}

// ============================================================================
// SYNC HISTORY ROW
// ============================================================================

interface SyncHistoryRowProps {
  sync: MarketplaceSyncHistory;
  integrationName?: string;
}

function SyncHistoryRow({ sync, integrationName }: SyncHistoryRowProps) {
  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed': return <CheckCircle className="w-4 h-4 text-green-600" />;
      case 'failed': return <XCircle className="w-4 h-4 text-red-600" />;
      case 'running': return <Loader2 className="w-4 h-4 text-blue-600 animate-spin" />;
      case 'partial': return <AlertCircle className="w-4 h-4 text-yellow-600" />;
      default: return <Clock className="w-4 h-4 text-gray-400" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'bg-green-100 text-green-700';
      case 'failed': return 'bg-red-100 text-red-700';
      case 'running': return 'bg-blue-100 text-blue-700';
      case 'partial': return 'bg-yellow-100 text-yellow-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  return (
    <tr className="hover:bg-gray-50">
      <td className="px-4 py-3">
        <div className="flex items-center gap-2">
          {getStatusIcon(sync.status)}
          <span className={`px-2 py-0.5 text-xs rounded ${getStatusColor(sync.status)}`}>
            {sync.status}
          </span>
        </div>
      </td>
      <td className="px-4 py-3 text-sm text-gray-600">
        {integrationName || sync.installation_id.slice(0, 8)}
      </td>
      <td className="px-4 py-3 text-sm text-gray-600">
        {sync.sync_type}
      </td>
      <td className="px-4 py-3 text-sm text-gray-600">
        {new Date(sync.started_at).toLocaleString()}
      </td>
      <td className="px-4 py-3 text-sm text-gray-600">
        {sync.duration_seconds ? `${sync.duration_seconds}s` : '-'}
      </td>
      <td className="px-4 py-3">
        <div className="flex items-center gap-3 text-sm">
          <span className="text-green-600">+{sync.records_created}</span>
          <span className="text-blue-600">~{sync.records_updated}</span>
          <span className="text-red-600">-{sync.records_deleted}</span>
          {sync.records_failed > 0 && (
            <span className="text-orange-600">!{sync.records_failed}</span>
          )}
        </div>
      </td>
    </tr>
  );
}

// ============================================================================
// INSTALL INTEGRATION MODAL
// ============================================================================

interface InstallModalProps {
  isOpen: boolean;
  onClose: () => void;
  integration: AvailableIntegration | null;
  onSubmit: (config: Record<string, string>) => void;
  isLoading: boolean;
}

function InstallModal({ isOpen, onClose, integration, onSubmit, isLoading }: InstallModalProps) {
  const [apiKey, setApiKey] = useState('');
  const [subdomain, setSubdomain] = useState('');

  useEffect(() => {
    setApiKey('');
    setSubdomain('');
  }, [integration]);

  if (!integration) return null;

  const handleSubmit = () => {
    if (integration.auth_type === 'oauth2') {
      onSubmit({});
    } else {
      onSubmit({ api_key: apiKey, subdomain });
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Install {integration.name}</DialogTitle>
          <DialogDescription>
            {integration.auth_type === 'oauth2'
              ? 'You will be redirected to authenticate with ' + integration.name
              : 'Enter your API credentials to connect ' + integration.name}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {integration.auth_type === 'oauth2' ? (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <Shield className="w-5 h-5 text-blue-600 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-blue-900">Secure OAuth Connection</p>
                  <p className="text-sm text-blue-700 mt-1">
                    Click Connect to securely authorize access to your {integration.name} account.
                    You will be redirected to {integration.name} to complete authentication.
                  </p>
                </div>
              </div>
            </div>
          ) : (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  API Key *
                </label>
                <input
                  type="password"
                  value={apiKey}
                  onChange={(e) => setApiKey(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter your API key"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Subdomain (if applicable)
                </label>
                <input
                  type="text"
                  value={subdomain}
                  onChange={(e) => setSubdomain(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="your-company"
                />
              </div>
              <p className="text-xs text-gray-500">
                Your credentials are encrypted and stored securely.
              </p>
            </>
          )}
        </div>

        <DialogFooter>
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={isLoading || (integration.auth_type === 'api_key' && !apiKey)}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2 disabled:opacity-50"
          >
            {isLoading && <Loader2 className="w-4 h-4 animate-spin" />}
            {integration.auth_type === 'oauth2' ? (
              <>
                <ExternalLink className="w-4 h-4" />
                Connect with OAuth
              </>
            ) : (
              'Connect'
            )}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ============================================================================
// MAIN PAGE COMPONENT
// ============================================================================

export function MarketplaceIntegrationsPage() {
  const { t } = useAppTranslation();
  const tabbed = useTabbedChrome();
  const [activeTab, setActiveTab] = useState<MarketplaceTab>('installed');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Data states
  const [installations, setInstallations] = useState<MarketplaceInstallation[]>([]);
  const [availableIntegrations, setAvailableIntegrations] = useState<AvailableIntegration[]>(FALLBACK_INTEGRATIONS);
  const [syncHistory, setSyncHistory] = useState<MarketplaceSyncHistory[]>([]);

  // Modal states
  const [showInstallModal, setShowInstallModal] = useState(false);
  const [selectedIntegration, setSelectedIntegration] = useState<AvailableIntegration | null>(null);
  const [installing, setInstalling] = useState(false);

  // Filters
  const [categoryFilter, setCategoryFilter] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState('');

  // Load data
  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [installationsRes, integrationsRes] = await Promise.all([
        marketplaceApi.getInstallations(),
        marketplaceApi.getAvailableIntegrations({ category: categoryFilter || undefined }),
      ]);

      if (installationsRes.success && installationsRes.data) {
        // Handle both array and object response formats
        const installationsData = Array.isArray(installationsRes.data)
          ? installationsRes.data
          : (installationsRes.data as any).installations || [];
        setInstallations(installationsData);
        // Load sync history for all installations
        if (installationsData.length > 0) {
          const historyPromises = installationsData.map((i: MarketplaceInstallation) =>
            marketplaceApi.getSyncHistory(i.id, { page_size: 5 })
          );
          const historyResults = await Promise.all(historyPromises);
          const allHistory = historyResults.flatMap(r => {
            if (!r.success || !r.data) return [];
            // Handle both array and object response formats
            const historyData = Array.isArray(r.data) ? r.data : (r.data as any).history || [];
            return historyData;
          });
          setSyncHistory(allHistory.sort((a, b) =>
            new Date(b.started_at).getTime() - new Date(a.started_at).getTime()
          ));
        }
      }
      if (integrationsRes.success && integrationsRes.data) {
        // Handle both array and object response formats
        const integrationsData = Array.isArray(integrationsRes.data)
          ? integrationsRes.data
          : (integrationsRes.data as any).integrations || [];
        setAvailableIntegrations(integrationsData);
      }
    } catch (err) {
      setError('Failed to load integrations');
      console.error('Error loading marketplace data:', err);
    } finally {
      setLoading(false);
    }
  }, [categoryFilter]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Handle install
  const handleInstall = async (config: Record<string, string>) => {
    if (!selectedIntegration) return;

    setInstalling(true);
    try {
      if (selectedIntegration.auth_type === 'oauth2') {
        // Get OAuth URL and redirect
        const res = await marketplaceApi.getOAuthUrl(selectedIntegration.id);
        if (res.success && res.data?.auth_url) {
          window.location.href = res.data.auth_url;
          return;
        }
      } else {
        // API key installation
        await marketplaceApi.createInstallation({
          integration_id: selectedIntegration.id,
          config,
        });
      }
      setShowInstallModal(false);
      setSelectedIntegration(null);
      loadData();
    } catch (err) {
      console.error('Error installing integration:', err);
    } finally {
      setInstalling(false);
    }
  };

  // Handle sync
  const handleSync = async (installationId: string) => {
    try {
      await marketplaceApi.triggerSync(installationId, 'incremental');
      loadData();
    } catch (err) {
      console.error('Error triggering sync:', err);
    }
  };

  // Handle pause/resume
  const handlePause = async (installationId: string) => {
    try {
      await marketplaceApi.pauseInstallation(installationId);
      loadData();
    } catch (err) {
      console.error('Error pausing installation:', err);
    }
  };

  const handleResume = async (installationId: string) => {
    try {
      await marketplaceApi.resumeInstallation(installationId);
      loadData();
    } catch (err) {
      console.error('Error resuming installation:', err);
    }
  };

  // Handle disconnect
  const handleDisconnect = async (installationId: string) => {
    if (!confirm('Are you sure you want to disconnect this integration?')) return;
    try {
      await marketplaceApi.deleteInstallation(installationId);
      loadData();
    } catch (err) {
      console.error('Error disconnecting integration:', err);
    }
  };

  const installedIds = new Set(installations.map(i => i.integration_id));
  const filteredIntegrations = availableIntegrations.filter(i =>
    (!searchQuery || i.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
     i.description.toLowerCase().includes(searchQuery.toLowerCase())) &&
    (!categoryFilter || i.category === categoryFilter)
  );

  const tabs = [
    { id: 'installed' as const, label: t('platform.marketplace.tabs.installed', 'Installed'), icon: Link2, count: installations.length },
    { id: 'available' as const, label: t('platform.marketplace.tabs.available', 'Available'), icon: Store, count: availableIntegrations.length },
    { id: 'sync-history' as const, label: t('platform.marketplace.tabs.syncHistory', 'Sync History'), icon: Activity, count: syncHistory.length },
  ];

  return (
    <div className={`min-h-screen bg-gray-50${productConfig.isTriveny ? ' triveny-platform' : ''}`}>
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            {!tabbed && <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 flex items-center gap-2"><Store className="h-6 w-6 text-blue-600" />{t('platform.marketplace.title', 'Marketplace Integrations')}</h1>}
            <p className="text-sm text-gray-500 mt-1">
              {t('platform.marketplace.description', 'Connect your HRIS, PEO, payroll, and benefits systems')}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={loadData}
              className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg"
            >
              <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 mt-4">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                activeTab === tab.id
                  ? 'bg-blue-100 text-blue-700'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
              <span className={`px-1.5 py-0.5 text-xs rounded ${
                activeTab === tab.id ? 'bg-blue-200 text-blue-800' : 'bg-gray-200 text-gray-600'
              }`}>
                {tab.count}
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="p-6">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center h-64 text-red-600">
            <AlertCircle className="w-12 h-12 mb-4" />
            <p>{error}</p>
            <button onClick={loadData} className="mt-4 px-4 py-2 bg-red-100 rounded-lg hover:bg-red-200">
              Retry
            </button>
          </div>
        ) : (
          <>
            {/* Installed Tab */}
            {activeTab === 'installed' && (
              <div>
                {installations.length === 0 ? (
                  <div className="text-center py-12">
                    <Store className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">No Integrations Installed</h3>
                    <p className="text-gray-500 mb-4">Browse available integrations to connect your systems.</p>
                    <button
                      onClick={() => setActiveTab('available')}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                    >
                      Browse Integrations
                    </button>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {installations.map((installation) => (
                      <InstallationCard
                        key={installation.id}
                        installation={installation}
                        onConfigure={() => console.log('Configure:', installation.id)}
                        onSync={() => handleSync(installation.id)}
                        onPause={() => handlePause(installation.id)}
                        onResume={() => handleResume(installation.id)}
                        onDisconnect={() => handleDisconnect(installation.id)}
                        onViewHistory={() => setActiveTab('sync-history')}
                      />
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Available Tab */}
            {activeTab === 'available' && (
              <div>
                {/* Filters */}
                <div className="bg-white rounded-lg border border-gray-200 p-4 mb-4">
                  <div className="flex flex-wrap items-center gap-4">
                    <div className="flex-1 min-w-[200px]">
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <input
                          type="text"
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                          placeholder="Search integrations..."
                          className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                    </div>
                    <select
                      value={categoryFilter}
                      onChange={(e) => setCategoryFilter(e.target.value)}
                      className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">All Categories</option>
                      <option value="hris">HRIS</option>
                      <option value="peo">PEO</option>
                      <option value="payroll">Payroll</option>
                      <option value="benefits">Benefits</option>
                      <option value="communication">Communication</option>
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {filteredIntegrations.map((integration) => (
                    <AvailableIntegrationCard
                      key={integration.id}
                      integration={integration}
                      isInstalled={installedIds.has(integration.id)}
                      onInstall={() => {
                        setSelectedIntegration(integration);
                        setShowInstallModal(true);
                      }}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Sync History Tab */}
            {activeTab === 'sync-history' && (
              <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Integration</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Started</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Duration</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Records</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {syncHistory.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="px-4 py-12 text-center text-gray-500">
                          No sync history yet. Install and sync an integration to see history.
                        </td>
                      </tr>
                    ) : (
                      syncHistory.map((sync) => (
                        <SyncHistoryRow
                          key={sync.id}
                          sync={sync}
                          integrationName={installations.find(i => i.id === sync.installation_id)?.integration_name}
                        />
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </div>

      {/* Install Modal */}
      <InstallModal
        isOpen={showInstallModal}
        onClose={() => { setShowInstallModal(false); setSelectedIntegration(null); }}
        integration={selectedIntegration}
        onSubmit={handleInstall}
        isLoading={installing}
      />
    </div>
  );
}

export default MarketplaceIntegrationsPage;